import { GetStaticProps } from 'next';

import {
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	MapsFiguresOverviewSingleFieldsFragment,
	PageInformationFieldsFragment
} from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetMapsFiguresOverview } from 'api/graphql-helper';

import { initialState as mapsFiguresInitialState } from 'redux/stores/maps-figures';
import { initialState as settingsInitialState } from 'redux/stores/settings';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import { BaseModule } from 'modules/base-module/base-module';
import IntroHeader from 'modules/intro-header/intro-header';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';
import { searchMapsFigures } from 'api/algolia';
import { MAPS_FIGURES_OVERVIEW_LIMIT } from 'modules/maps-figures-overview/maps-figures-overview';

interface IMapsFiguresOverviewPageProps extends IBasePageProps {
	mapsFiguresOverview?: MapsFiguresOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const MapsFiguresOverviewPage = ({
	pageInformation,
	mapsFiguresOverview
}: IMapsFiguresOverviewPageProps): React.JSX.Element => {
	const modules = [mapsFiguresOverview?.mapsFiguresOverview, ...(mapsFiguresOverview?.modules || [])];
	const seoModule = mapsFiguresOverview?.seo;

	const headerDefaultOptions = {
		backgroundColor: mapsFiguresOverview?.header
			?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
		padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
	};

	return (
		<Layout navbar={pageInformation?.navbar} footer={pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={pageInformation?.seo} />
			<BaseModule defaultModuleOptions={headerDefaultOptions}>
				<IntroHeader module={mapsFiguresOverview?.header} slug={ROUTES.MAPS_AND_FIGURES.fullPath} />
			</BaseModule>
			<ModuleBuilder modules={modules || []} slug={ROUTES.MAPS_AND_FIGURES.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetMapsFiguresOverview({ variables: { locale, publicationState } }, client);
		const { pageInformation, mapsFiguresOverview, themes_connection: themes } = result.props.data;

		const { results: mapsFigures, total: mapsFiguresCount } = await searchMapsFigures(
			'',
			{
				themesIds: []
			},
			{
				sort: 'asc',
				page: 1,
				limit: MAPS_FIGURES_OVERVIEW_LIMIT,
				locale
			}
		);

		if (mapsFiguresOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				mapsFiguresOverview: mapsFiguresOverview.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'maps-figures-overview',
						contentTypeId: mapsFiguresOverview.data?.documentId
					},
					mapsFigures: {
						...mapsFiguresInitialState,
						mapsFigures,
						mapsFiguresCount
					},
					themes: {
						themes: themes?.data || [],
						totalThemes: themes?.pageInfo.total || 0
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in maps and figures overview', error);

		return {
			notFound: true
		};
	}
};

export default MapsFiguresOverviewPage;
